import {useState} from 'react';
import {Card, Box, Typography, Link} from '@mui/material';
import HiristBannerComponent from '@jobseeker/lotus/components/HiristBanner';
import {Image} from '@jobseeker/lotus/components/Image';
import {cleanUrl} from '@jobseeker/lotus/utils';
import dayjs from 'dayjs';
import {NewUserIcon} from '@jobseeker/lotus/assets/images';
type SkillsTagType = {
  tagId: number;
  tagName: string;
};

type RecruiterDataType = {
  recruiterTags: Array<SkillsTagType>;
  linkedInUrl: string;
  name: string;
  lastLogin?: Date;
  lastActive: Date | string;
  designation: string;
  organisation: string;
  location: string;
  image: string;
  skillsTag: {
    tagId: number;
    tagName: string;
  };
};
const Header = ({recruiterData}: {recruiterData: RecruiterDataType}) => {
  const {recruiterTags} = recruiterData;
  const [tagsShowMore, setTagsShowMore] = useState(false);
  let recruiterTagsEdited;
  if (recruiterTags && recruiterTags.length) {
    if (recruiterTags.length > 10 && !tagsShowMore) {
      recruiterTagsEdited = recruiterTags.slice(0, 10);
    } else {
      recruiterTagsEdited = recruiterTags;
    }
  }
  const card = (
    <HiristBannerComponent>
      <Card
        sx={{
          display: 'flex',
          flexDirection: {xxs: 'column', md: 'row'},
          alignItems: 'center',
          justifyContent: {xxs: 'center', md: 'flex-start'},
          textAlign: 'left',
          width: '100%',
          margin: '0',
          borderRadius: '0',
          backgroundRepeat: 'repeat',
          backgroundPositionX: '-300px',
          minHeight: {xxs: '250px', md: '210px', lg: '180px'}, // Responsive min-height
          padding: {
            xxs: '2px 0px',
            md: '2px 6px',
            lg: '2px 6px',
          }, // Responsive padding
          backgroundColor: 'transparent',
          position: {xxs: 'relative', md: 'static'}, // Adjusts position
        }}
      >
        <Box
          sx={{
            display: 'inline-block',
            alignSelf: {xxs: 'center', md: 'flex-start'},
            verticalAlign: 'top',
            width: {xxs: '100%', sm: '100%', md: '10%', lg: '10%'}, // Responsive width
            position: 'relative',
            textAlign: {xxs: 'center', md: 'left'}, // Adjust text alignment
            marginTop: {xxs: '40px', sm: '40px', md: '0'},
            marginLeft: {lg: '20px'},
          }}
        >
          <Image
            width="80"
            height="80"
            src={recruiterData?.image?.trim() || NewUserIcon}
            alt="recruiter-profile-image"
            style={{
              borderRadius: '50%',
            }}
          />
          {recruiterData && recruiterData.linkedInUrl ? (
            <Link
              href={recruiterData.linkedInUrl}
              target="_blank"
              sx={{
                color: '#fff',
                borderRadius: '50%', // Circular shape
                position: 'absolute',
                left: '-3px',
                top: '-3px',
                fontSize: '30px',
                textDecoration: 'none', // Removes default underline
                display: 'inline-flex', // Ensures proper alignment
                alignItems: 'center',
                justifyContent: 'center',
              }}
            ></Link>
          ) : (
            ''
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: {xxs: 'center', md: 'normal'},
            gap: '4px',
            margin: '0px',
            width: {xxs: '100%', md: '85%'},
            alignSelf: 'flex-start',
          }}
          data-testid="recruiter-header"
        >
          <Typography
            component="h2"
            variant="h5"
            color="white"
            sx={{
              marginTop: {xxs: '18px', md: '0'},
            }}
          >
            {recruiterData?.name ?? ''}
            <Typography
              variant="body3"
              component="span"
              sx={{
                position: {md: 'static', xxs: 'absolute'},
                top: {xxs: '10px'},
                right: {xxs: '10px'},
                marginTop: {xxs: '3px'},
                fontSize: {xxs: '12px'},
                marginLeft: {xxs: '0', md: '5px'},
              }}
            >
              {recruiterData && recruiterData.lastActive
                ? `( Last Active: ${
                    dayjs(recruiterData.lastActive).isValid()
                      ? dayjs(recruiterData.lastActive).format('DD MMM YYYY')
                      : recruiterData.lastActive
                  })`
                : ''}
            </Typography>
          </Typography>
          {recruiterData && recruiterData.designation ? (
            <Typography
              component="h1"
              variant="body2"
              color="white"
              sx={{
                marginTop: '-3px',
                '& a': {
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline !important',
                  },
                },
              }}
            >
              {recruiterData.designation} at {recruiterData.organisation}
            </Typography>
          ) : (
            ''
          )}
          {recruiterData && recruiterData.location ? (
            <Typography
              variant="body2"
              color="white"
              sx={{
                '& a': {
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline !important',
                  },
                },
                marginTop: '-3px',
              }}
            >
              {recruiterData.location}
            </Typography>
          ) : (
            ''
          )}
          <hr
            style={{
              height: '1px',
              color: 'white',
              width: '100%',
              opacity: '15%',
              marginTop: '6px !important',
              marginBottom: '8px !important',
            }}
          />
          {recruiterTags && (
            <Box>
              <Typography
                component="h2"
                color="white"
                variant="body2"
                sx={{
                  margin: {xxs: '4px 0px', md: '5px 0px'},
                }}
              >
                Posted jobs with following skills
              </Typography>
              <Box>
                {recruiterTagsEdited
                  ? recruiterTagsEdited.map((skillsTag: SkillsTagType) => {
                      return (
                        <Link
                          key={skillsTag.tagId}
                          href={`/k/${cleanUrl(skillsTag.tagName)}-jobs`}
                          sx={{
                            color: 'white',
                            border: '1px solid white',
                            borderRadius: '20px',
                            padding: '3px 10px',
                            margin: '0px 5px 8px 0px',
                            fontSize: '12px',
                            whiteSpace: 'nowrap',
                            display: 'inline-block',
                            textDecoration: 'none !important',
                            cursor: 'pointer',
                          }}
                          target="_blank"
                        >
                          {skillsTag.tagName}
                        </Link>
                      );
                    })
                  : ''}
                {recruiterTags && recruiterTags.length > 10 ? (
                  <Link
                    sx={{
                      color: 'white',
                      border: '1px solid white',
                      borderRadius: '20px',
                      padding: '3px 10px',
                      margin: '0px 5px 8px 0px',
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      display: 'inline-block',
                      textDecoration: 'none !important',
                      cursor: 'pointer',
                    }}
                    onClick={e => {
                      e.preventDefault();
                      setTagsShowMore(tagsShowMore => !tagsShowMore);
                    }}
                  >
                    {tagsShowMore
                      ? 'Show less'
                      : `+ ${recruiterTags.length - 10} more`}
                  </Link>
                ) : (
                  ''
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Card>
    </HiristBannerComponent>
  );

  return (
    <Box sx={{minWidth: 275}}>
      <Card variant="outlined">{card}</Card>
    </Box>
  );
};

export default Header;
